/**
 * Notification Realtime Subscription Hook
 * Subscribes to the notifications table for real-time updates.
 * 
 * Enhanced with dispatch matrix (Phase 8):
 * - Invalidates notification queries on ALL events
 * - Dispatches entity-scoped cache invalidations based on notification_type
 * - Fixes G1 (user-posts staleness), G2 (DELETE handling), M1 (entity_type destructuring)
 */

import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { useRealtimeSubscription } from './useRealtimeSubscription';
import { useRealtimeInvalidations } from '@/lib/realtimeInvalidations';
import { getChannelName } from '@/lib/realtimeChannelNames';
import { logger } from '@/lib/logger';

interface NotificationRecord {
  notification_type?: string;
  entity_id?: string;
  entity_type?: string;
  reference_post_id?: string;
  reference_photo_id?: string;
  reference_parent_comment_id?: string;
}

interface RealtimePayload {
  eventType: string;
  new?: NotificationRecord;
  old?: NotificationRecord;
}

/**
 * Subscribe to real-time notification updates for the current user.
 * Should be called once in a high-level layout component (e.g., DashboardLayout).
 * 
 * @param myUsername - Current user's username for user-posts invalidation (G1 fix)
 */
export function useNotificationRealtime(myUsername?: string) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const invalidate = useRealtimeInvalidations();

  const handleEvent = useCallback((payload: unknown) => {
    const { eventType, new: newRecord, old: oldRecord } = payload as RealtimePayload;

    // G2 fix: Use old record for DELETE events (new is empty on DELETE)
    const record = eventType === 'DELETE' ? oldRecord : newRecord;
    if (!record) return;

    // M1 fix: Destructure entity_type for mention dispatch
    const { notification_type, entity_type, entity_id, reference_post_id, reference_photo_id } = record;

    // Always invalidate notification queries first
    invalidate.notifications();

    // G1 fix: Invalidate user-posts for engagement events so /profile stays fresh
    const engagementTypes = ['post_like', 'photo_like', 'comment', 'reply', 'comment_like'];
    const isEngagement = notification_type && engagementTypes.includes(notification_type);

    if (isEngagement && myUsername) {
      invalidate.userPosts(myUsername);
    }

    // Dispatch entity-scoped invalidations based on notification_type
    // NOTE: No squareFeed invalidation here — feed stability is preserved.
    // Feed data refreshes on next navigation via refetchOnMount: true.
    switch (notification_type) {
      case 'post_like':
        if (entity_id) {
          invalidate.postEngagement(entity_id);
        }
        break;

      case 'photo_like':
        if (entity_id) {
          invalidate.photoEngagement(entity_id, reference_post_id ?? undefined);
        }
        break;

      case 'comment':
      case 'reply': {
        // entity_id is the comment_id
        const postId = reference_post_id;
        const photoId = reference_photo_id;
        if (entity_id) {
          invalidate.commentData(entity_id, photoId ?? undefined, postId ?? undefined);
        }
        if (postId) {
          invalidate.postEngagement(postId);
        }
        if (photoId) {
          invalidate.photoEngagement(photoId, postId ?? undefined);
        }
        break;
      }

      case 'comment_like': {
        const postId = reference_post_id;
        const photoId = reference_photo_id;
        if (entity_id) {
          invalidate.commentData(entity_id, photoId ?? undefined, postId ?? undefined);
        }
        break;
      }

      case 'mention':
      case 'mention_edit': {
        if (entity_type === 'post' && entity_id) {
          invalidate.postEngagement(entity_id);
        } else if ((entity_type === 'comment' || entity_type === 'reply') && reference_post_id) {
          invalidate.postEngagement(reference_post_id);
          if (reference_photo_id) {
            invalidate.photoEngagement(reference_photo_id, reference_post_id ?? undefined);
          }
        }
        break;
      }

      case 'connection_request':
      case 'connection_accepted':
        invalidate.connectionData();
        break;

      default:
        logger.debug('[NotificationRealtime] Unknown notification_type', { notification_type });
        break;
    }
  }, [invalidate, myUsername]);

  useRealtimeSubscription({
    channelName: getChannelName('notifications', 'user', currentUserId || 'disabled'),
    table: 'notifications',
    filter: currentUserId ? `recipient_id=eq.${currentUserId}` : undefined,
    eventType: '*',
    enabled: !!currentUserId,
    onEvent: handleEvent,
  });
}
